import { Stack, Typography, Select, MenuItem, Button, FormControl, InputLabel, Box, CircularProgress, Card, CardContent } from '@mui/material';
import { useEffect, useState } from 'react';
import { useGetDebugUsersQuery, useLoginAsMutation } from '~/features/user/model/api/login-api';
import { setCookie } from '~/shared/utils/cookie';
import { useDispatch } from 'react-redux';
import { setToken } from '~/features/user/model/slices/user-slice';
import GoogleIcon from '@mui/icons-material/Google';

export const Login = () => {
  const dispatch = useDispatch();
  const backendUrl = (import.meta.env.VITE_BACKEND_URL || import.meta.env.VITE_API_URL || '').replace(/\/+$/, '');
  const oauthUrl = import.meta.env.DEV ? '/api/oauth' : `${backendUrl}/api/oauth`;
  const shouldLogToken = import.meta.env.DEV || String(import.meta.env.VITE_DEV_AUTH).toLowerCase() === 'true';
  const { data: usersData, error, isLoading } = useGetDebugUsersQuery(undefined);
  const [loginAs, { isLoading: isLoggingIn }] = useLoginAsMutation();
  const [selectedUser, setSelectedUser] = useState('');

  const handleLogin = async () => {
    if (!selectedUser) return;
    
    try {
      const resp = await loginAs({ user_id: selectedUser }).unwrap();
      if (resp.token) {
        setCookie('auth-token', resp.token, { 'max-age': 86400 });
        dispatch(setToken(resp.token));
        if (shouldLogToken) {
          console.log('auth-token:', resp.token);
        }
        location.href = '/';
      }
    } catch (e) {
      console.error(e);
    }
  };

  if (isLoading) {
    return (
      <Stack flexDirection="column" alignItems="center" gap={'12px'} height="100vh" justifyContent="center">
         <CircularProgress size={60} />
         <Typography>Загрузка</Typography>
      </Stack>
    );
  }

  // Production/Dev style (when debug users fail)
  if (error || (usersData?.data && usersData.data.length === 0)) {
     return (
            <Box
              sx={{
                minHeight: '100dvh',        // лучше чем 100vh
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                px: 2,                      // отступы на мобильных
                bgcolor: 'background.default',
              }}
            >
              <Card
                sx={{
                  width: '100%',
                  maxWidth: 400,            // адаптив
                  bgcolor: '#1E293B',
                  color: 'white',
                  borderRadius: 2,
                  boxShadow:
                    '0 4px 6px -1px rgba(0,0,0,0.1), 0 2px 4px -1px rgba(0,0,0,0.06)',
                }}
              >
                <CardContent
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 3,
                    py: 4,
                  }}
                >
                  <Typography variant="h5" fontWeight={600}>
                    {import.meta.env.VITE_APP_NAME || 'Mango CPA'}
                  </Typography>

                  <Button
                    href={oauthUrl}
                    variant="contained"
                    startIcon={<GoogleIcon />}
                    sx={{
                      bgcolor: '#4285F4',
                      textTransform: 'none',
                      fontWeight: 500,
                      py: 1.5,
                      width: '100%',
                      borderRadius: 10,
                      '&:hover': { bgcolor: '#3367D6' },
                    }}
                  >
                    Войти через Google
                  </Button>
                </CardContent>
              </Card>
            </Box>
          );
  }

  // If we have users, show the selection (Local Dev)
  if (usersData?.data && usersData.data.length > 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
        <Stack gap={2} width={300}>
            <Typography variant="h5">Local</Typography>
            <FormControl fullWidth>
                <InputLabel>Пользователи</InputLabel>
                <Select
                    value={selectedUser}
                    label="Пользователь"
                    onChange={(e) => setSelectedUser(String(e.target.value))}
                >
                    {usersData.data.map((user: any) => (
                        <MenuItem key={user.id} value={user.id}>
                            {user.name} ({user.email})
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>
            <Button 
                variant="contained" 
                onClick={handleLogin}
                disabled={!selectedUser || isLoggingIn}
            >
                {isLoggingIn ? 'Вход...' : 'Войти'}
            </Button>
        </Stack>
      </Box>
    );
  }

  return (
    <Stack flexDirection="row" alignItems="center" gap={'12px'}>
        <CircularProgress size={60} />
        <Typography variant="h2" component="h2">
            Redirect to google.com...
        </Typography>
    </Stack>
  );
};

export const AuthCallback = () => {
  const dispatch = useDispatch();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const token = params.get('token');
    if (token) {
      setCookie('auth-token', token, { 'max-age': 86400 });
      dispatch(setToken(token));
      history.replaceState({}, '', '/');
      location.href = '/accounts';
      return;
    }
    location.href = '/login';
  }, [dispatch]);

  return (
    <Stack flexDirection="column" alignItems="center" gap={'12px'} height="100vh" justifyContent="center">
      <CircularProgress size={60} />
      <Typography>Авторизация...</Typography>
    </Stack>
  );
};
